import React, { useState, useEffect } from 'react';
import { getAllPlatforms } from '../platform/controller/platform-controller';

const OrderFilters = ({
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    onApplyFilters,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    syncroFilter,
    setSyncroFilter,
    platformFilter,      // Add platform filter prop
    setPlatformFilter    // Add platform filter setter prop
}) => {
    // Local state to track input values before applying them
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
    const [localStartDate, setLocalStartDate] = useState(startDate || '');
    const [localEndDate, setLocalEndDate] = useState(endDate || '');
    const [localSyncroFilter, setLocalSyncroFilter] = useState(syncroFilter || 'all');
    const [localStatusFilter, setLocalStatusFilter] = useState(statusFilter || 'all');
    const [localPlatformFilter, setLocalPlatformFilter] = useState(platformFilter || 'all'); // Add platform state
    const [platforms, setPlatforms] = useState([]); // Platform options
    const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(false);

    // Fetch platforms on component mount
    useEffect(() => {
        const fetchPlatformsData = async () => {
            setIsLoadingPlatforms(true);
            try {
                const platformsData = await getAllPlatforms();
                setPlatforms(platformsData || []);
            } catch (error) {
                console.error('Error fetching platforms:', error);
                setPlatforms([]);
            } finally {
                setIsLoadingPlatforms(false);
            }
        };

        fetchPlatformsData();
    }, []);

    // Update local state when props change (like when coming from notification)
    useEffect(() => {
        setLocalSearchTerm(searchTerm || '');
        setLocalStartDate(startDate || '');
        setLocalEndDate(endDate || '');
        setLocalSyncroFilter(syncroFilter || 'all');
        setLocalStatusFilter(statusFilter || 'all');
        setLocalPlatformFilter(platformFilter || 'all'); // Update platform filter
    }, [searchTerm, startDate, endDate, syncroFilter, statusFilter, platformFilter]);

    // Fix the handleApplyFilters function
    const handleApplyFilters = () => {
        // Update parent component's state with local values
        setSearchTerm(localSearchTerm);

        if (setStartDate) setStartDate(localStartDate || '');
        if (setEndDate) setEndDate(localEndDate || '');
        if (setSyncroFilter) setSyncroFilter(localSyncroFilter === 'all' ? '' : localSyncroFilter);
        if (setStatusFilter) setStatusFilter(localStatusFilter);
        if (setPlatformFilter) setPlatformFilter(localPlatformFilter === 'all' ? '' : localPlatformFilter); // Update platform filter

        // Log what we're sending (for debugging)
        console.log("OrderFilters sending these parameters:", {
            search: localSearchTerm || '',
            status: localStatusFilter,
            startDate: localStartDate || '',
            endDate: localEndDate || '',
            syncro: localSyncroFilter === 'all' ? '' : localSyncroFilter,
            platform: localPlatformFilter === 'all' ? '' : localPlatformFilter // Add platform to log
        });

        // Make sure platform filter is included in the parameters!
        onApplyFilters(
            localSearchTerm || '',
            localStatusFilter,
            localStartDate || '',
            localEndDate || '',
            localSyncroFilter === 'all' ? '' : localSyncroFilter,
            localPlatformFilter === 'all' ? '' : localPlatformFilter // Add platform parameter
        );
    };

    // Handler for keydown event on search input to support Enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
    // Reset local state
    setLocalSearchTerm('');
    setLocalStartDate('');
    setLocalEndDate('');
    setLocalSyncroFilter('all');
    setLocalStatusFilter('all');
    setLocalPlatformFilter('all'); // Reset platform filter

    // Update parent component state with empty values
    setSearchTerm('');
    if (setStartDate) setStartDate('');
    if (setEndDate) setEndDate('');
    if (setSyncroFilter) setSyncroFilter('');
    if (setStatusFilter) setStatusFilter('');
    if (setPlatformFilter) setPlatformFilter(''); // Reset platform filter

    // Apply the cleared filters with empty strings (not 'all')
    onApplyFilters('', '', '', '', '', ''); // Add empty platform parameter

    // Clear notification state from browser history
    window.history.replaceState({}, document.title);
};

    // Format today's date as YYYY-MM-DD for max date attribute
    const today = new Date().toISOString().split('T')[0];
    console.log('those are the platforms:', platforms);
    return (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-4">
            {/* Search input - reduced width to accommodate platform filter */}
            <div className="sm:col-span-3 md:col-span-3 lg:col-span-3">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm h-[38px]"
                        placeholder="Rechercher par ID, nom, téléphone..."
                        value={localSearchTerm}
                        onChange={(e) => setLocalSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {localSearchTerm && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <button
                                onClick={() => setLocalSearchTerm('')}
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                type="button"
                            >
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Platform selector */}
            <div className="sm:col-span-2 md:col-span-2 lg:col-span-2">
                <div className="relative">
                    <label htmlFor="platform-filter" className="block text-xs text-gray-500 mb-1 sm:absolute sm:-top-5 sm:left-0">
                        Plateforme
                    </label>
                    <select
                        id="platform-filter"
                        value={localPlatformFilter}
                        onChange={(e) => setLocalPlatformFilter(e.target.value)}
                        disabled={isLoadingPlatforms}
                        className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm rounded-md h-[38px] disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 bg-white"
                    >
                        <option value="all" className="text-gray-900 bg-white">
                            {isLoadingPlatforms ? 'Chargement...' : 'Toutes'}
                        </option>
                        {platforms.map((platform) => (
                            <option key={platform.id} value={platform.id} className="text-gray-900 bg-white">
                                {platform.name} {platform.typePlateform ? ` (${platform.typePlateform})` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Syncro status selector */}
            <div className="sm:col-span-2 md:col-span-2 lg:col-span-2">
                <div className="relative">
                    <label htmlFor="syncro-filter" className="block text-xs text-gray-500 mb-1 sm:absolute sm:-top-5 sm:left-0">
                        Statut Syncro
                    </label>
                    <select
                        id="syncro-filter"
                        value={localSyncroFilter}
                        onChange={(e) => setLocalSyncroFilter(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm rounded-md h-[38px]"
                    >
                        <option value="null">Tous</option>
                        <option value="true">Synchronisé</option>
                        <option value="false">Non synchronisé</option>
                    </select>
                </div>
            </div>

            {/* Date Range Fields - adjusted width to accommodate platform filter */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-2 sm:col-span-3 md:col-span-3 lg:col-span-3">
                {/* Start Date */}
                <div className="col-span-1">
                    <div className="relative">
                        <label htmlFor="start-date" className="block text-xs text-gray-500 mb-1 sm:absolute sm:-top-5 sm:left-0">
                            Date début
                        </label>
                        <input
                            id="start-date"
                            type="date"
                            className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm h-[38px]"
                            value={localStartDate}
                            onChange={(e) => setLocalStartDate(e.target.value)}
                            max={localEndDate || today}
                        />
                    </div>
                </div>

                {/* End Date */}
                <div className="col-span-1">
                    <div className="relative">
                        <label htmlFor="end-date" className="block text-xs text-gray-500 mb-1 sm:absolute sm:-top-5 sm:left-0">
                            Date fin
                        </label>
                        <input
                            id="end-date"
                            type="date"
                            className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm h-[38px]"
                            value={localEndDate}
                            onChange={(e) => setLocalEndDate(e.target.value)}
                            min={localStartDate}
                            max={today}
                        />
                    </div>
                </div>
            </div>

            {/* Action buttons */}
            <div className="flex space-x-2 sm:col-span-12 md:col-span-2 lg:col-span-2 justify-between sm:justify-start">
                <button
                    type="button"
                    onClick={handleApplyFilters}
                    className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] h-[38px] flex-grow sm:flex-grow-0 sm:min-w-[80px]"
                >
                    <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span className="inline">Filtrer</span>
                </button>
                {(localSearchTerm || localStartDate || localEndDate || localSyncroFilter !== 'all' || localPlatformFilter !== 'all') && (
                    <button
                        type="button"
                        onClick={handleClearFilters}
                        className="inline-flex items-center px-2 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] h-[38px]"
                        title="Effacer les filtres"
                    >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default OrderFilters;